import { forwardRef, type CSSProperties } from 'react';
import { cn } from '../../lib/cn';
import { resolveColor, type SColor } from '../../lib/color';
import { ICONS, type SIconName } from './icons.gen';

export interface SIconProps {
  /** 아이콘명 */
  name: SIconName;
  /** 아이콘 크기(px 또는 CSS 길이) */
  size?: number | string;
  /** 아이콘 색상. 팔레트 키(`grey_65`, `red_95` …) 또는 임의 CSS 색상. 미지정 시 currentColor 상속 */
  color?: SColor;
  /** 회전 각도 */
  rotate?: 0 | 90 | 180 | 270;
  /** 접근성 라벨. 없으면 aria-hidden 처리 */
  label?: string;
  className?: string;
  style?: CSSProperties;
}

/**
 * SIcon — sd-icon 포팅.
 * 94개 SVG(icons.gen.tsx)를 currentColor 기반으로 렌더한다.
 */
export const SIcon = forwardRef<HTMLSpanElement, SIconProps>(function SIcon(
  { name, size = 24, color, rotate, label, className, style },
  ref,
) {
  const Svg = ICONS[name];
  if (!Svg) return null;

  const rotateClass = rotate
    ? { 90: 'rotate-90', 180: 'rotate-180', 270: '-rotate-90' }[rotate]
    : undefined;

  return (
    <span
      ref={ref}
      className={cn('inline-flex items-center justify-center', rotateClass, className)}
      style={{ color: resolveColor(color), ...style }}
      role={label ? 'img' : undefined}
      aria-label={label}
      aria-hidden={label ? undefined : true}
    >
      <Svg size={size} />
    </span>
  );
});
